import { Injectable, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, Observable } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { RouterQueryParams } from '../../../shared/router-query-params.service'
import { filter, map } from 'rxjs/operators'
import { MexusViewMode } from './mexus-view-mode'

@Injectable({
  providedIn: 'root',
})
export class ViewModeContextService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private readonly _activeViewMode = new BehaviorSubject<MexusViewMode>(MexusViewMode.PREVIEW)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private routerQueryParams = inject(RouterQueryParams)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    // Check for view mode query param on all router levels
    this.routerQueryParams.params
      .pipe(
        map((params) => params['viewMode']),
        filter((viewModeParam) => !!viewModeParam)
      )
      .subscribe({
        next: (viewMode) => this.setViewMode(viewMode, false),
        error: (err) => this.log.error('Failed to set view mode from query params!', err),
      })
  }

  /***************************************************************************
   *                                                                         *
   * Company Getters / Setters                                               *
   *                                                                         *
   **************************************************************************/

  public get viewMode$(): Observable<MexusViewMode> {
    return this._activeViewMode.pipe(filter((viewMode) => !!viewMode))
  }

  public get viewMode(): MexusViewMode {
    return this._activeViewMode.getValue()
  }

  public setViewMode(viewMode: MexusViewMode, updateQueryParam = true): void {
    if (viewMode != this.viewMode) {
      this._activeViewMode.next(viewMode)
      if (updateQueryParam) {
        this.updateViewModeQueryParam(viewMode)
      }
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private updateViewModeQueryParam(viewMode: MexusViewMode): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { viewMode: viewMode },
      replaceUrl: true, // if true, won't add a new browser history entry
      queryParamsHandling: 'merge',
    })
  }
}
